import { useEffect, useMemo, useState, useRef } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsLMS';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import axios from 'axios';
import Modal from './CertificacionesModal';
import { getSessionUserId, getSessionEmpresaId, isSessionSuperAdmin } from '../../../../../utils/SessionUser';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import AlertDialog from '../../Shared/AlertDialog';
import { useResourcePermissions } from '../../../../../utils/useResourcePermissions';

const PAGE_SIZE = 10;
const nombreTabla = "Certificaciones";
const endpoint = ENDPOINTS.certificaciones;
const endpointById = ENDPOINTS.certificacionesById;
const modelo = Models.certificaciones;

const maps = {
  mapCreate: (f) => ({
    idCurso: Number(f.idCurso) || 0,
    certificacionUrl: f.certificacionUrl,
    idFirma: Number(f.idFirma) || 0,
    activo: true, // Siempre activo por defecto
  }),
  mapUpdate: (id, f) => ({
    idCertificacion: id,
    idCurso: Number(f.idCurso) || 0,
    certificacionUrl: f.certificacionUrl,
    idFirma: Number(f.idFirma) || 0,
    activo: true, // Siempre activo
  }),
};

export default function Catalog() {
  const { puedeCrear, puedeEditar, puedeEliminar } = useResourcePermissions('Certificaciones');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [firmas, setFirmas] = useState([]);
  const [instructores, setInstructores] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [peticiones, setPeticiones] = useState([]);
  const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

  const triggerAlert = (message, variant = 'error', title = 'Error') => {
    setModalOpen(false);
    setDeleteOpen(false);
    setAlertConfig({ open: true, message, variant, title });
  };
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [editData, setEditData] = useState(null);
  const [page, setPage] = useState(1);

  const editorRef = useRef(null);

  useEffect(() => {
    if (modalOpen && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [modalOpen, selectedId]);

  // Cargar catálogos de Cursos, Firmas, Instructores, Empleados y Personas
  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const idUsuarioSesion = getSessionUserId();
        const idEmpresa = getSessionEmpresaId();
        const esSuperAdmin = isSessionSuperAdmin();
        const [cursosRes, firmasRes, instructoresRes, empleadosRes, personasRes, peticionesRes] = await Promise.all([
          axios.get(`${ENDPOINTS.cursosAdmin}?idEmpresa=${idEmpresa || 0}&esSuperAdmin=${esSuperAdmin}`, { withCredentials: true }),
          axios.get(ENDPOINTS.firmas, { withCredentials: true }),
          axios.get(ENDPOINTS.instructor, { withCredentials: true }),
          axios.get(ENDPOINTS.empleados, { withCredentials: true }),
          axios.get(ENDPOINTS.personas, { withCredentials: true }),
          axios.get(ENDPOINTS.peticionesCertificado, { withCredentials: true }).catch(() => ({ data: [] }))
        ]);
        setCursos((cursosRes.data || []).map(Models.cursos));
        setFirmas((firmasRes.data || []).map(Models.firmas));
        setInstructores((instructoresRes.data || []).map(Models.instructor));
        setEmpleados((empleadosRes.data || []).map(Models.empleado));
        setPersonas((personasRes.data || []).map(Models.personas));
        // Normalizar peticiones y pendientes para asegurar idCurso uniforme
        setPeticiones((peticionesRes.data || []).map(p => ({
          ...p,
          idCurso: p.idCurso || p.IdCurso,
          idUsuarioSolicitado: p.idUsuarioSolicitado || p.IdUsuarioSolicitado,
          atendido: p.atendido ?? p.Atendido
        })));
      } catch (e) {
        console.error('Error cargando catálogos:', e);
      }
    };
    loadCatalogs();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(endpoint, { withCredentials: true });
      setRows((res.data || []).map((x) => modelo(x)));
    } catch (e) {
      console.error(e);
      triggerAlert('Error al cargar información');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // Helper para obtener nombre del curso
  const getCursoNombre = (idCurso) => {
    const curso = cursos.find(c => c.idCurso === idCurso);
    return curso?.nombre || `Curso ${idCurso}`;
  };

  // Helper para obtener nombre de la persona asociada a la firma
  const getPersonaNombreFromFirma = (idFirma) => {
    // 1. Buscar la firma
    const firma = firmas.find(f => f.idFirma === idFirma);
    if (!firma) return `Firma ${idFirma}`;

    // 2. Buscar el instructor por idEmpleado de la firma
    const instructor = instructores.find(i => i.idEmpleado === firma.idEmpleado);
    if (!instructor) return `Firma ${idFirma}`;

    // 3. Buscar el empleado
    const empleado = empleados.find(e => e.idEmpleado === firma.idEmpleado);
    if (!empleado) return `Firma ${idFirma}`;

    // 4. Buscar la persona
    const persona = personas.find(p => p.idPersona === empleado.idPersona);
    if (!persona) return `Firma ${idFirma}`;

    // 5. Retornar nombre completo
    return `${persona.nombre} ${persona.apellidoPaterno} ${persona.apellidoMaterno || ''}`.trim();
  };


  const filtered = useMemo(() => {
    const isSuperAdmin = isSessionSuperAdmin();
    const sessionEmpresaId = getSessionEmpresaId();

    // 1. Aislamiento Multi-Tenant
    let data = rows;
    if (!isSuperAdmin) {
      data = rows.filter(r => r.idEmpresa === sessionEmpresaId || !r.idEmpresa);
    }

    const q = query.trim().toLowerCase();
    if (!q) return data;
    return data.filter((r) => {
      const cursoNombre = (r.cursoNombre || getCursoNombre(r.idCurso)).toLowerCase();
      const url = (r.certificacionUrl || '').toLowerCase();
      const personaNombre = (r.nombreFirma || getPersonaNombreFromFirma(r.idFirma)).toLowerCase();
      const empresaNombre = (r.empresaNombre || '').toLowerCase();
      return cursoNombre.includes(q) || url.includes(q) || personaNombre.includes(q) || empresaNombre.includes(q);
    });
  }, [rows, query, cursos, firmas, instructores, empleados, personas]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const cursosForSelect = useMemo(() => {
    // Obtener los IDs de cursos que ya tienen una certificación registrada en la tabla
    const existingCertCourseIds = rows.map(r => r.idCurso);
    const idUsuario = getSessionUserId();

    return cursos.filter(c => {
      // Si estamos editando, permitimos el curso actual
      if (modalMode === 'edit' && editData && c.idCurso === editData.idCurso) return true;
      
      // Mostrar solo cursos que NO tengan ya una certificación en el catálogo
      if (existingCertCourseIds.includes(c.idCurso)) return false;

      // Filtrar cursos que tengan una solicitud de revisión activa para el usuario en sesión
      const hasActivePetition = peticiones.some(p => 
          Number(p.idCurso) === c.idCurso && 
          !p.atendido && 
          Number(p.idUsuarioSolicitado) === Number(idUsuario)
      );

      return hasActivePetition;
    });
  }, [cursos, rows, modalMode, editData, peticiones]);

  const handleNew = () => {
    setSelectedId(null);
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
  };

  const handleEdit = (id) => {
    const r = rows.find(x => x.idCertificacion === id);
    if (!r) {
      triggerAlert('No se encontró la fila en memoria.');
      return;
    }
    setSelectedId(id);
    setEditData(r);
    setModalMode('edit');
    setModalOpen(true);
  };

  const askDelete = async (id, name) => {
    // Buscar la certificación y su curso asociado
    const cert = rows.find(x => x.idCertificacion === id);
    if (cert) {
      setLoading(true);
      try {
        const curso = cursos.find(c => c.idCurso === cert.idCurso);
        
        // 1. Si el curso ya no está activo, permitimos borrar sin más drama
        if (curso && !curso.activo) {
          setDeleteTarget({ id, name, idCurso: cert.idCurso });
          setDeleteOpen(true);
          return;
        }

        // 2. Si el curso está activo, validamos si tiene alumnos cursándolo
        const resAlumnos = await axios.get(ENDPOINTS.estudiantesCursoByCurso(cert.idCurso), { withCredentials: true });
        const alumnosActivos = (resAlumnos.data || []).filter(a => a.activo || a.Activo);

        if (alumnosActivos.length > 0) {
          triggerAlert(`No se puede eliminar la certificación porque el curso "${curso?.nombre}" tiene ${alumnosActivos.length} alumnos cursándolo.`, 'error', 'Acción restringida');
          return;
        }

        // 3. Si no tiene alumnos, permitimos borrar
        setDeleteTarget({ 
            id, 
            name, 
            idCurso: cert.idCurso 
        });
        setDeleteOpen(true);
      } catch (err) {
        console.error('Error al validar alumnos:', err);
        triggerAlert('Error al validar estado de los alumnos del curso.');
      } finally {
        setLoading(false);
      }
    }
  };

  const doDelete = async () => {
    try {
      setLoading(true);
      const { id } = deleteTarget;
      // 1. Eliminar la certificación (baja lógica vía SP)
      await axios.delete(endpointById(id), { withCredentials: true });
      console.log(`[CertificacionesCatalog] Certificación ${id} eliminada.`);

      setDeleteOpen(false);
      await loadData();
      triggerAlert('Certificación eliminada correctamente.', 'success', 'Operación exitosa');
    } catch (e) {
      console.error(e);
      triggerAlert('Error durante el proceso de eliminación');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, mode) => {
    try {
      setLoading(true);
      const idUsuario = getSessionUserId();

      if (mode === 'new') {
        const res = await axios.post(endpoint, maps.mapCreate(payload), { withCredentials: true });
        const newId = res?.data?.idCertificacion ?? (typeof res?.data === 'number' ? res.data : null);

        // Actualizar la petición de certificado
        const idCurso = Number(payload.idCurso);
        const peticion = peticiones.find(p =>
          Number(p.idCurso || p.IdCurso) === idCurso &&
          !(p.atendido || p.Atendido)
        );

        if (peticion) {
          // Extraer el ID correcto de la petición (el log del usuario muestra 'idPeticion')
          const idP = peticion.idPeticion || peticion.IdPeticion || peticion.idPeticionCertificado || peticion.IdPeticionCertificado;

          // Construir el objeto EXACTO que espera el Procedure LMS.sp_PeticionCertificado_Actualizar
          // Usamos PascalCase para que coincida con los parámetros del SP
          const updatePayload = {
            IdPeticion: Number(idP),
            IdCurso: Number(peticion.idCurso || peticion.IdCurso),
            IdUsuarioSolicitado: Number(peticion.idUsuarioSolicitado || peticion.IdUsuarioSolicitado),
            IdUsuarioSolicitante: Number(peticion.idUsuarioSolicitante || peticion.IdUsuarioSolicitante),
            FechaPeticion: peticion.fechaPeticion || peticion.FechaPeticion,
            Atendido: true,
            IdUsuarioAtendio: Number(idUsuario) || 0,
            Activo: true
          };

          console.log('[CertificacionesCatalog] Enviando update de petición:', updatePayload);
          await axios.put(ENDPOINTS.peticionesCertificado, updatePayload, { withCredentials: true });
          console.log(`[CertificacionesCatalog] Petición ${idP} marcada como atendida.`);
        }

        if (newId) { } // Placeholder if needed, otherwise remove
      } else {
        await axios.put(endpoint, maps.mapUpdate(selectedId, payload), { withCredentials: true });
      }
      setModalOpen(false);
      await loadData();

      // Recargar peticiones para reflejar cambios en el select
      const petRes = await axios.get(ENDPOINTS.peticionesCertificado, { withCredentials: true }).catch(() => ({ data: [] }));
      setPeticiones(petRes.data || []);

    } catch (e) {
      console.error(e);
      triggerAlert('Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >{label}</button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <input
            className="roles-search"
            placeholder="Buscar por curso, empresa, URL o instructor…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          />
          {puedeCrear && <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>}
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Curso</th>
                  <th>Instructor</th>
                  <th>Empresa</th>
                  {puedeEditar && <th style={{ textAlign: 'center' }}>Editar</th>}
                  {puedeEliminar && <th style={{ textAlign: 'center' }}>Borrar</th>}
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => (
                  <tr key={r.idCertificacion}>
                    <td>{r.cursoNombre || getCursoNombre(r.idCurso)}</td>
                    <td>{r.nombreFirma || getPersonaNombreFromFirma(r.idFirma)}</td>
                    <td>{r.empresaNombre}</td>
                    {puedeEditar && (
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="rk-iconbtn rk-iconbtn--edit"
                          onClick={() => handleEdit(r.idCertificacion)}
                          aria-label="Editar"
                          title="Editar"
                        >
                          <img src={sigmaEdit} alt="" className="rk-ico" />
                        </button>
                      </td>
                    )}
                    {puedeEliminar && (
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="rk-iconbtn rk-iconbtn--del"
                          onClick={() => {
                            const name = r.certificacionUrl || `Certificación ${r.idCertificacion}`;
                            askDelete(r.idCertificacion, name);
                          }}
                          aria-label="Borrar"
                          title="Borrar"
                        >
                          <img src={sigmaDelete} alt="" className="rk-ico" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {!loading && paged.length === 0 && (<tr><td colSpan={3 + (puedeEditar ? 1 : 0) + (puedeEliminar ? 1 : 0)}>Sin resultados.</td></tr>)}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0
              ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
              : <span>0 resultados</span>}
          </div>
          <div className="roles-pagination-right">
            <PageBtn label="‹" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} />
            {pageNumbers.map(n => (<PageBtn key={n} label={n} onClick={() => setPage(n)} active={n === page} />))}
            <PageBtn label="›" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
          </div>
        </div>

        {modalOpen && (
          <div className="roles-editor-slot" ref={editorRef}>
            <Modal
              open={modalOpen}
              mode={modalMode}
              data={editData}
              cursos={cursosForSelect}
              firmas={firmas}
              instructores={instructores}
              empleados={empleados}
              personas={personas}
              inline
              onClose={() => setModalOpen(false)}
              onSubmit={handleSubmit}
              loading={loading}
              triggerAlert={triggerAlert}
            />
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />

      <AlertDialog
        open={alertConfig.open}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
        title={alertConfig.title}
        message={alertConfig.message}
        variant={alertConfig.variant}
      />
    </div>
  );
}
